import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { Spin, Result } from "antd"; // Added Result
import AuthWrapper from "../AuthWrapper";
import "./styles/signUp.css";
import "./styles/transitions.css";
import "./styles/structure.css";
import UserTypeStep from "./signUpSteps/UserTypeStep";
import UsernameStep from "./signUpSteps/UsernameStep";
import AccountDetailsStep from "./signUpSteps/AccountDetailsStep";
import BusinessDetailsStep from "./signUpSteps/BusinessDetailsStep";
import TopicsQuestionsStep from "./signUpSteps/TopicsQuestionsStep";
import ReviewPlatformsStep from "./signUpSteps/ReviewPlatformsStep";
import { API_URLS } from "../../config"; // Import API_URLS

const reviewPlatforms = [
  { id: 'google', name: 'Google Maps' },
  { id: 'yelp', name: 'Yelp' },
  { id: 'thefork', name: 'The Fork' },
  { id: 'trustpilot', name: 'Trustpilot' },
  { id: 'tripadvisor', name: 'TripAdvisor' }
];

function SignUp() {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  // Simplified state - we'll just track direction for animations
  const [slideDirection, setSlideDirection] = useState('forward');
  const formRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    userType: "business_owner",
    name: "",
    email: "",
    password: "",
    repeatPassword: "",
    businessName: "",
    headerImage: null,
    logoImage: null,
    questions: Array(7).fill().map(() => ({ text: "", topic: "" })),
    platformLinks: {} // Only use platformLinks
  });
  const [error, setError] = useState(""); // For API errors
  const [stepValidationError, setStepValidationError] = useState(""); // For step validation errors
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loading state for account creation
  const [signupSuccess, setSignupSuccess] = useState(false); // State for signup success

  useEffect(() => {
    /* Body, html overflow: hidden !important; */
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Print form data when changed
  useEffect(() => {
    console.log(formData);
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear step validation error when form data changes, as user might be fixing it
    if (stepValidationError) {
      setStepValidationError("");
    }
  };

  const handleSubmit = async () => {
    console.log(formData);
    setIsLoading(true); // Start loading
    try {
      // Use registerBusiness URL from config
      const response = await fetch(API_URLS.registerBusiness, { // Use config
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        console.log("Registration successful:", data);
        if (data && data.token) {
          localStorage.setItem('token', data.token);
        } else {
          console.warn('Token not found in registration response.');
        }
        if (data && data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('businessId', data.user.businessId);
          localStorage.setItem('businessName', data.user.businessName);
          localStorage.setItem('name', data.user.userName);
        } else {
          console.warn('User data not found in registration response.');
        }
        setIsLoading(false); // Stop loading before showing success
        setSignupSuccess(true); // Show success message
        // Navigation will be handled by useEffect watching signupSuccess or a button in Result
        setTimeout(() => navigate("/"), 3000); // Auto-redirect after 3s
      } else {
        setError(data.message || "Registration failed");
        setIsLoading(false); // Stop loading on failure
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      setIsLoading(false); // Stop loading on error
    } 
    // Removed finally block as setIsLoading(false) is handled in try/catch
  };

  useEffect(() => {
    // set the property of the auth-left to register
    const authLeft = document.querySelector('.auth-left');
    if (authLeft) {
      authLeft.classList.remove('password-reset');
      authLeft.classList.add('register');
    }
  }, []);

  // Validation functions
  const validateUsernameStep = (data) => {
    if (!data.name || data.name.trim().length === 0) return "Name is required.";
    if (data.name.trim().length < 3) return "Name must be at least 3 characters long.";
    if (data.name.trim().length > 50) return "Name cannot exceed 50 characters.";
    return "";
  };

  const validateAccountDetailsStep = (data) => {
    if (!data.email || data.email.trim().length === 0) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) return "Invalid email format.";
    if (!data.password) return "Password is required.";
    const passRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passRegex.test(data.password)) return "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, and a number.";
    if (data.password !== data.repeatPassword) return "Passwords do not match.";
    return "";
  };

  const validateBusinessDetailsStep = (data) => {
    if (!data.businessName || data.businessName.trim().length === 0) return "Business name is required.";
    if (!data.headerImage) return "Header image is required.";
    if (!data.logoImage) return "Logo image is required.";
    // Note: Image size validation is primarily handled within BusinessDetailsStep before updateFormData
    return "";
  };

  const validateTopicsQuestionsStep = (data) => {
    const filledQuestions = (data.questions || []).filter(q => q.topic && q.topic.trim() !== "" && q.text && q.text.trim() !== "").length;
    if (filledQuestions < 3) return "Please add at least 3 topics and their corresponding questions.";
    return "";
  };

  const validateReviewPlatformsStep = (data) => {
    const selectedPlatformIds = Object.keys(data.platformLinks || {});
    if (selectedPlatformIds.length === 0) {
      // This case should ideally be caught by isStepDataValid for button disabling,
      // but as a fallback for direct validation:
      return "Please select at least one review platform.";
    }

    const providedLinks = selectedPlatformIds.filter(id => data.platformLinks[id] && data.platformLinks[id].trim() !== "");
    if (providedLinks.length < 1) {
      return "Please provide a link for at least one selected platform.";
    }

    // const urlRegex = /^https?:\/\/.+/; // Old regex
    const urlRegex = /^https?:\/\/[^/]+\/.*$/; // New regex: ensures http(s)://, domain, and a path starting with /

    for (const platformId of providedLinks) {
      const link = data.platformLinks[platformId].trim();
      if (!urlRegex.test(link)) {
        const platform = reviewPlatforms.find(p => p.id === platformId);
        const platformName = platform ? platform.name : platformId;
        return `Invalid URL for ${platformName}. It must start with http:// or https:// and include a path (e.g., example.com/page or example.com/).`;
      }
    }
    return "";
  };

  // Function to check if current step data is valid for ENABLING the next button
  const isStepDataValid = (stepIndex, data) => {
    switch (stepIndex) {
      case 0: // UserTypeStep - always valid to proceed
        return true;
      case 1: // UsernameStep - enabled if name field is not empty
        return !!(data.name && data.name.trim() !== "");
      case 2: // AccountDetailsStep - enabled if email and password fields are not empty
        return !!(data.email && data.email.trim() !== "" && data.password && data.password !== "" && data.repeatPassword && data.repeatPassword !== "");
      case 3: // BusinessDetailsStep - enabled if business name is present
        return !!(data.businessName && data.businessName.trim() !== "");
      case 4: // TopicsQuestionsStep - original logic: at least 3 questions
        return validateTopicsQuestionsStep(data) === "";
      case 5: // ReviewPlatformsStep - enabled if at least one platform is selected AND its link is filled
        const platformLinks = data.platformLinks || {};
        // At least one platform with a non-empty link
        return Object.values(platformLinks).some(link => link && link.trim() !== "");
      default:
        return true;
    }
  };


  const navigateWithTransition = (path) => {
    setIsExiting(true);
    const form = document.querySelector('.form');
    form.classList.remove('entered');
    form.classList.add('exiting');
    
    // For login transitions, adjust auth-left class
    const authLeft = document.querySelector('.auth-left');
    if (path === '/login') {
      authLeft.classList.remove('register');
      setTimeout(() => {
        // find signUp-box and add exit class
        const signUpBox = document.querySelector('.signUp-box');
        if (signUpBox) {
          signUpBox.classList.add('exit');
        }
      }, 150); // Match the CSS transition duration
    } else if (path === '/password-reset') {
      authLeft.classList.remove('register');
      authLeft.classList.add('password-reset');
      setTimeout(() => {
        // find signUp-box and add exit class
        const signUpBox = document.querySelector('.signUp-box');
        if (signUpBox) {
          signUpBox.classList.add('exit');
        }
      }, 150); // Match the CSS transition duration
    }
    
    setTimeout(() => {
      navigate(path);
    }, 650); // Match the CSS transition duration
  };

  // Simplified step navigation functions
  const goToPreviousStep = () => {
    // replaced setTimeout with direct calls
    setSlideDirection('backward');
    setStepValidationError(""); // Clear errors when moving
    setCurrentStep(prev => prev - 1);
  };

  const goToNextStep = () => {
    setStepValidationError(""); // Clear previous errors
    let validationMessage = "";

    switch (currentStep) {
      case 1: // UsernameStep (index 1 of steps array)
        validationMessage = validateUsernameStep(formData);
        break;
      case 2: // AccountDetailsStep (index 2)
        validationMessage = validateAccountDetailsStep(formData);
        break;
      case 3: // BusinessDetailsStep (index 3)
        validationMessage = validateBusinessDetailsStep(formData);
        break;
      case 4: // TopicsQuestionsStep (index 4)
        validationMessage = validateTopicsQuestionsStep(formData);
        break;
      case 5: // ReviewPlatformsStep (index 5 - last step before submit)
        validationMessage = validateReviewPlatformsStep(formData);
        break;
      default:
        // No validation for UserTypeStep (index 0) or if step is out of bounds
        break;
    }

    if (validationMessage) {
      setStepValidationError(validationMessage);
      return; // Stop progression
    }

    // replaced setTimeout with direct calls
    if (currentStep === steps.length - 1) {
      handleSubmit();
    } else {
      setSlideDirection('forward');
      setCurrentStep(prev => prev + 1);
    }
  };

  const steps = [
    <UserTypeStep key="step1" formData={formData} updateFormData={updateFormData} />,
    <UsernameStep key="step2" formData={formData} updateFormData={updateFormData} />,
    <AccountDetailsStep
      key="step3"
      formData={formData}
      updateFormData={updateFormData}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
    />,
    <BusinessDetailsStep
      key="step4"
      formData={formData}
      updateFormData={updateFormData}
    />,
    <TopicsQuestionsStep key="step5" formData={formData} updateFormData={updateFormData} />,
    <ReviewPlatformsStep
      key="step6"
      formData={formData}
      updateFormData={updateFormData}
      reviewPlatforms={reviewPlatforms}
    />,
  ];

  return (
    <AuthWrapper rightContent={null}>
      <CSSTransition 
        in={!isExiting} 
        appear={true} 
        timeout={650}
        classNames="page-transition"
      >
        <div className="glass-box-auth signUp-box">
          {signupSuccess ? (
            <Result
              status="success"
              title="Account Created Successfully!"
              subTitle="You will be redirected to the dashboard in a few moments."
              // Optionally, add a button for manual redirection if auto-redirect is not preferred
              // extra={[
              //   <Button type="primary" key="console" onClick={() => navigateWithTransition("/")}>
              //     Go to Dashboard
              //   </Button>,
              // ]}
            />
          ) : (
            <>
              <h1 className="title">Sign Up</h1>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>

              <div className="signUp-step-container">
                {isLoading && (
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 10,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '16px' // Match parent's border radius if any
                    }}
                  >
                    <Spin size="large" tip="Creating your account..." />
                  </div>
                )}
                <div className="step-container" style={{ opacity: isLoading ? 0.5 : 1 }}>
                  <form
                    className="form entered"
                    onSubmit={(e) => e.preventDefault()}
                    ref={formRef}
                  >
                    {/* Render the current step directly without complex transitions */}
                    <div className={`step-wrapper ${slideDirection}`} key={currentStep}>
                      {steps[currentStep]}
                    </div>
                  </form>
                </div>
                {stepValidationError && !isLoading && <p className="error-message step-validation-error">{stepValidationError}</p>}
              </div>

              {!isLoading && ( /* Hide navigation and errors when loading globally or on success */
                <div className="navigation-links-container">
                  <div className="signup-step-navigation">
                    {currentStep > 0 && (
                      <button
                        type="button"
                        className="signup-nav-button"
                        onClick={goToPreviousStep}
                      >
                        Previous
                      </button>
                    )}
                    <button
                      type="button"
                      className="signup-nav-button"
                      onClick={goToNextStep}
                      disabled={!isStepDataValid(currentStep, formData)}
                    >
                      {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
                    </button>
                  </div>

                  <div className="transition-links">
                    <p className="register">
                      Already have an account?{` `}
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          navigateWithTransition('/login');
                        }}
                        href="/login"
                        className="register-link"
                      >
                        Login
                      </a>
                    </p>
                  </div>
                  {error && <p className="error">{error}</p>}
                </div>
              )}
            </>
          )}
        </div>
      </CSSTransition>
    </AuthWrapper>
  );
}

export default SignUp;